import { PropsWithChildren, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
// eslint-disable-next-line import/no-unresolved
import type { Content } from '@plone/types';
import {
  NormalizableContent,
  transformToCard,
} from '@fhnw/helpers/DesignSystem/cardTransformers';
import { RichText } from '@fhnw/components/RichText';
import type { BlockWrapperDataProps } from '../BlockWrapper/BlockWrapper';
import { BlockWrapper } from '../BlockWrapper/BlockWrapper';
import { messages } from './messages';
import type { CardProps } from '@designsystem/stories/03-molecules/Card';
import { Modules } from '@designsystem/stories/04-organisms/Modules';
import {
  CATEGORY_CONTENT_FIELDS,
  defaultModuleGroups,
  type ModuleCategory,
  type ModuleEntryData,
  type ModuleGroupData,
} from './groups';
import { uidsMissingSchool, useResolvedModules } from './hooks';

export interface BlockDataProps extends BlockWrapperDataProps {
  title?: string;
  /** Pre-#1717 block-maintained entries, parked in the neutral group. */
  modules?: ModuleEntryData[];
  moduleGroups?: ModuleGroupData[];
  degree?: string;
  showTeaserImage?: boolean;
  showUniversity?: boolean;
}

interface ViewProps {
  data: BlockDataProps;
  content: Content;
  isEdit?: boolean;
  block: string;
  onChangeBlock: (blockId: string, newData: any) => void;
}

const Factory = (props: PropsWithChildren) => <>{props.children}</>;

const View = (props: ViewProps) => {
  const intl = useIntl();
  const _content = useSelector(
    (state: { content: { data: Content } }) => state.content.data,
  );
  const content =
    props?.content && (props.content?.['@id'] || props.content?.parent)
      ? props.content
      : _content;
  const data = { ...props?.data };

  let updated = false;
  if (!('title' in data)) {
    data.title = intl.formatMessage(messages.Title);
    updated = true;
  }
  if (!data.moduleGroups?.length) {
    data.moduleGroups = defaultModuleGroups().map((group) => ({
      ...group,
      title: intl.formatMessage(messages[group.category]),
    }));
    updated = true;
  }

  useEffect(() => {
    if (updated && props?.isEdit && props.onChangeBlock) {
      props.onChangeBlock(props.block, data);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const visibleGroups = data.moduleGroups
    .filter((group) => group.showGroup !== false)
    .map((group) => ({
      group,
      entries: group.category
        ? (
            (content?.[CATEGORY_CONTENT_FIELDS[group.category]] ||
              []) as Content[]
          ).map((module: Content) => ({
            module,
            category: group.category,
          }))
        : group.modules || [],
    }));

  const showTeaserImage = data.showTeaserImage !== false;
  const showUniversity = data.showUniversity === true;

  const resolved = useResolvedModules(
    showUniversity
      ? visibleGroups.flatMap(({ entries }) =>
          uidsMissingSchool(entries as ModuleEntryData[]),
        )
      : [],
    props?.isEdit,
  );

  const toCard = (
    module: Content | undefined,
    category?: ModuleCategory,
  ): CardProps | null => {
    if (!module) {
      return null;
    }

    const item = transformToCard(
      module as NormalizableContent,
      intl,
      'product',
    );
    item.titleTag = 'h5';
    if (!showTeaserImage) {
      delete item.image;
    }
    // Without a university context the card adapter fills the info slot with
    // the school row from `university_tokens` — the only info this block gets.
    if (!showUniversity) {
      delete item.info;
    }
    if (category) {
      item.tags = [{ label: intl.formatMessage(messages[category]) }];
    }

    return item;
  };

  const groups = visibleGroups
    .map(({ group, entries }) => ({
      group,
      items: (entries as ModuleEntryData[])
        .map((entry) => {
          const module = entry?.module;
          const enriched = module?.UID ? resolved[module.UID] : undefined;
          return toCard(
            enriched ? { ...module, ...enriched } : module,
            entry?.category,
          );
        })
        .filter((item): item is CardProps => item !== null),
    }))
    // Standard groups only structure their aggregated modules; groups without
    // a category may stand as text-only (e.g. a master module).
    .filter(({ group, items }) =>
      group.category
        ? items.length > 0
        : items.length > 0 || group.title || group.description,
    )
    .map(({ group, items }) => ({
      id: group['@id'],
      title: group.title,
      description: group.description ? (
        <RichText text={group.description} />
      ) : undefined,
      items,
    }));

  if (!props?.isEdit && !groups.length) {
    return null;
  }

  return (
    <BlockWrapper
      isEdit={props?.isEdit}
      block={props?.block}
      onChangeBlock={props?.onChangeBlock}
      data={data}
      factory={Factory}
    >
      {data?.title && <h3>{data.title}</h3>}
      {/* An empty footer string still renders the closing arrow line (#1717) */}
      <Modules groups={groups} footer={data?.degree ?? ''} />
    </BlockWrapper>
  );
};

export default View;
